import { 
  doc, 
  updateDoc, 
  arrayUnion,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  PaymentSchedule, 
  PaymentInstallment, 
  Opportunity, 
  CustomerCompany 
} from '../types';

/**
 * Payment Schedule Service
 * Manages payment schedules and installments for customer revenue tracking
 */
export const paymentScheduleService = {
  /**
   * Generate monthly installments for a payment schedule
   */
  generateMonthlyInstallments(
    totalValue: number,
    startMonth: string,
    numberOfMonths: number,
    revenueType: 'recurring' | 'one_time'
  ): PaymentInstallment[] {
    const installments: PaymentInstallment[] = [];
    const monthlyAmount = numberOfMonths > 0 ? totalValue / numberOfMonths : totalValue;
    
    const [startYear, startMonthNum] = startMonth.split('-').map(Number);
    let currentYear = startYear;
    let currentMonth = startMonthNum;
    
    for (let i = 0; i < numberOfMonths; i++) {
      const monthStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
      
      installments.push({
        month: monthStr,
        amount: Math.round(monthlyAmount * 100) / 100, // Round to 2 decimals
        isPaid: false
      });
      
      // Move to next month
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    
    return installments;
  },

  /**
   * Create payment schedule from won opportunity
   */
  async createPaymentScheduleFromOpportunity(
    opportunity: Opportunity,
    customer: CustomerCompany,
    installmentMonths?: number,
    userId?: string
  ): Promise<PaymentSchedule> {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    // Determine revenue type from product or default to one_time
    const revenueType = opportunity.product?.pricing?.model || 'one_time';
    
    // Determine number of installments
    let numberOfMonths = installmentMonths || opportunity.billingTermMonths || 12;
    if (revenueType === 'one_time' && !installmentMonths) {
      numberOfMonths = 1; // Default one-time to single payment unless specified otherwise
    }
    
    // Calculate end month
    const [startYear, startMonthNum] = currentMonth.split('-').map(Number);
    let endYear = startYear;
    let endMonth = startMonthNum + numberOfMonths - 1;
    while (endMonth > 12) {
      endMonth -= 12;
      endYear++;
    }
    const endMonthStr = `${endYear}-${String(endMonth).padStart(2, '0')}`;
    
    // Generate installments
    const installments = this.generateMonthlyInstallments(
      opportunity.value || 0,
      currentMonth,
      numberOfMonths,
      revenueType
    );
    
    const schedule: PaymentSchedule = {
      id: `schedule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      sourceOpportunityId: opportunity.id,
      revenueType,
      totalContractValue: opportunity.value || 0,
      productId: opportunity.productId,
      productName: opportunity.product?.name,
      installments,
      startMonth: currentMonth,
      endMonth: endMonthStr,
      isActive: true,
      createdAt: now,
      createdBy: userId || 'system',
      notes: `Created from opportunity: ${opportunity.title}`
    };
    
    return schedule;
  },

  /**
   * Add payment schedule to customer in Firestore
   */
  async addScheduleToCustomer(
    customerId: string,
    schedule: PaymentSchedule
  ): Promise<void> {
    try {
      const customerRef = doc(db, 'companies', customerId);
      
      // Convert dates to Timestamps for Firestore
      const scheduleForFirestore = {
        ...schedule,
        createdAt: Timestamp.fromDate(schedule.createdAt),
        installments: schedule.installments.map(inst => ({
          ...inst,
          paidDate: inst.paidDate ? Timestamp.fromDate(inst.paidDate) : undefined
        }))
      };
      
      await updateDoc(customerRef, {
        paymentSchedules: arrayUnion(scheduleForFirestore)
      });
    } catch (error) {
      console.error('Error adding payment schedule to customer:', error);
      throw error;
    }
  },

  /**
   * Update a specific installment's paid status
   */
  async updateInstallmentPaidStatus(
    customerId: string,
    scheduleId: string,
    month: string,
    isPaid: boolean,
    paidDate?: Date
  ): Promise<void> {
    try {
      const customerRef = doc(db, 'companies', customerId);
      
      // Note: This is a simplified version. In production, you'd need to fetch the customer,
      // update the specific installment in the array, and write back the entire array.
      // Firestore doesn't support nested array updates directly.
      
      console.warn('updateInstallmentPaidStatus: Full implementation requires fetching and updating entire paymentSchedules array');
      
      // For now, we'll note this needs to be implemented with a full read-modify-write cycle
      throw new Error('Not yet implemented - requires full array update');
    } catch (error) {
      console.error('Error updating installment paid status:', error);
      throw error;
    }
  },

  /**
   * Calculate total revenue for a customer in a specific month
   * Combines payment schedules and manual overrides
   */
  calculateTotalRevenueForMonth(
    customer: CustomerCompany,
    month: string
  ): number {
    // Priority 1: Manual override
    if (customer.monthlyRevenue && customer.monthlyRevenue[month] !== undefined) {
      return customer.monthlyRevenue[month];
    }
    
    // Priority 2: Sum from payment schedules
    if (!customer.paymentSchedules || customer.paymentSchedules.length === 0) {
      return 0;
    }
    
    let total = 0;
    
    for (const schedule of customer.paymentSchedules) {
      if (!schedule.isActive) continue;
      
      // Find installment for this month
      const installment = schedule.installments.find(inst => inst.month === month);
      if (installment) {
        total += installment.amount;
      }
    }
    
    return total;
  },

  /**
   * Get all payment schedules for a customer
   */
  getCustomerPaymentSchedules(customer: CustomerCompany): PaymentSchedule[] {
    return customer.paymentSchedules || [];
  },

  /**
   * Get active payment schedules only
   */
  getActiveSchedules(customer: CustomerCompany): PaymentSchedule[] {
    return (customer.paymentSchedules || []).filter(s => s.isActive);
  },

  /**
   * Calculate total contract value across all active schedules
   */
  calculateTotalContractValue(customer: CustomerCompany): number {
    return this.getActiveSchedules(customer)
      .reduce((sum, schedule) => sum + schedule.totalContractValue, 0);
  },

  /**
   * Get revenue breakdown by product for a specific month
   */
  calculateRevenueByProductForMonth(
    customer: CustomerCompany,
    month: string
  ): Record<string, number> {
    const byProduct: Record<string, number> = {};
    
    if (!customer.paymentSchedules) return byProduct;
    
    for (const schedule of customer.paymentSchedules) {
      if (!schedule.isActive) continue;
      if (!schedule.productId) continue;
      
      const installment = schedule.installments.find(inst => inst.month === month);
      if (installment) {
        byProduct[schedule.productId] = (byProduct[schedule.productId] || 0) + installment.amount;
      }
    }
    
    return byProduct;
  },

  /**
   * Create a manual payment schedule (not from opportunity)
   */
  createManualSchedule(
    totalValue: number,
    revenueType: 'recurring' | 'one_time',
    startMonth: string,
    numberOfMonths: number,
    productId?: string,
    productName?: string,
    userId?: string,
    notes?: string
  ): PaymentSchedule {
    const now = new Date();
    
    // Calculate end month
    const [startYear, startMonthNum] = startMonth.split('-').map(Number);
    let endYear = startYear;
    let endMonth = startMonthNum + numberOfMonths - 1;
    while (endMonth > 12) {
      endMonth -= 12;
      endYear++;
    }
    const endMonthStr = `${endYear}-${String(endMonth).padStart(2, '0')}`;
    
    const installments = this.generateMonthlyInstallments(
      totalValue,
      startMonth,
      numberOfMonths,
      revenueType
    );
    
    return {
      id: `schedule_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      revenueType,
      totalContractValue: totalValue,
      productId,
      productName,
      installments,
      startMonth,
      endMonth: endMonthStr,
      isActive: true,
      createdAt: now,
      createdBy: userId || 'manual',
      notes: notes || 'Manually created schedule'
    };
  },

  /**
   * Deactivate a payment schedule
   */
  async deactivateSchedule(
    customerId: string,
    scheduleId: string
  ): Promise<void> {
    // Note: Similar to updateInstallmentPaidStatus, this requires a full read-modify-write cycle
    console.warn('deactivateSchedule: Full implementation requires fetching and updating entire paymentSchedules array');
    throw new Error('Not yet implemented - requires full array update');
  },

  /**
   * Format month string for display
   */
  formatMonthDisplay(month: string): string {
    const [year, monthNum] = month.split('-');
    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    return `${monthNames[parseInt(monthNum) - 1]} ${year}`;
  }
};

